// Blog content schema:
//   id:          unique slug used in the URL  (e.g. "what-is-tawakkul")
//   title:       the blog headline
//   subtitle:    optional secondary line shown below the title
//   author:      writer name shown on the article
//   date:        publish date (YYYY-MM-DD) — used for sorting and display
//   readTime:    approximate minutes to read
//   excerpt:     short summary shown on cards and previews
//   coverGradient: optional CSS gradient used as the card/article cover background
//   coverIcon:   optional emoji shown on the gradient cover
//   tags:        array of topic tags shown on the article
//   content:     ordered array of content blocks. Supported block types:
//                  - { type: 'heading',    text: '...' }            // main section heading
//                  - { type: 'subheading', text: '...' }            // smaller heading
//                  - { type: 'paragraph',  text: '...' }            // inline markdown allowed
//                  - { type: 'quote',      text: '...', cite?: '...' }
//                  - { type: 'verse',      text: '...', cite: '...' }   // Quran verses
//                  - { type: 'hadith',     text: '...', cite: '...' }   // Hadith
//                  - { type: 'list',       items: ['...', '...'] }
//                  - { type: 'divider' }
//
// Inline rich-text in any `text` field:
//   **bold**      -> <strong>bold</strong>
//   *italic*      -> <em>italic</em>
//   __underline__ -> <u>underline</u>

export const blogs = [
  {
    id: 'low-imaan-a-sign-of-faith',
    title: 'What if your "low" Imaan is actually a sign of faith?',
    subtitle: 'Understanding the spiritual journey through Ghazali and Ibn al-Qayyim',
    author: 'Dr. Samaviya Alamgir',
    date: '2026-05-19',
    readTime: 9,
    excerpt:
      'Have you ever felt a deep ache in your heart because your prayers feel mechanical? What you call "low Imaan" may actually be a hidden blessing — and the great scholars have a far more merciful explanation than most of us were ever taught.',
    coverGradient: 'linear-gradient(135deg, #0b2545 0%, #1a3a5f 60%, #b68d40 100%)',
    coverIcon: '🤍',
    tags: ['Imaan', 'Spirituality', 'Heart'],
    content: [
      {
        type: 'paragraph',
        text:
          'Have you ever felt a deep ache in your heart because you feel like your prayers are mechanical? You once had spiritual presence, *khushu* (stillness) and a heart filled with *noor* while prostrating, but now it feels empty inside. You make dua, and you feel like it stopped at the ceiling and never reached the sky because you feel nothing?',
      },
      {
        type: 'paragraph',
        text:
          'And then a fear creeps into your heart saying, *"Is my Imaan low? Am I losing my faith? Am I being heedless?"*',
      },
      {
        type: 'paragraph',
        text:
          'But what if I tell you that this mere **fear of losing the quality of your Imaan is a direct blessing from Allah**. Because it shows that your heart still cares, and is still hurting with the crippling fear of losing the light.',
      },
      {
        type: 'paragraph',
        text:
          'The Islamic scholarly tradition, particularly **Imam Ghazali**, **Ibn al-Qayyim al-Jawziyyah**, and the broader Sufi intellectual tradition, has a great deal to say about exactly this experience. And what they say is far more merciful and far more nuanced than most of us have been taught.',
      },

      { type: 'heading', text: 'The Foundation of Iman' },
      {
        type: 'paragraph',
        text:
          'Most people think Imaan (faith) is one single entity, a unified feeling. Either you have it, or you are deprived of it. Either it is strong, or it is weak. But **Imam Ghazali** in *"Ihya Ulum al-Din"*, one of the most comprehensive works on Islamic spirituality ever written, breaks iman into distinct, separate layers.',
      },

      { type: 'subheading', text: 'Tasdiq al-Qalb' },
      {
        type: 'paragraph',
        text:
          'The **"deep affirmation of the heart"** — the roots of belief. This is your conviction that Allah exists, is One, and that this religion is the one true religion. It lives at the deepest level and does not flicker with your mood.',
      },

      { type: 'subheading', text: 'Iqrar bil-Lisan' },
      {
        type: 'paragraph',
        text:
          'The **"acknowledgement of the tongue"** — the Shahadah, your duas, your dhikr. This is how the internal becomes expressed in the world.',
      },

      { type: 'subheading', text: 'Amal bil-Jawarih' },
      {
        type: 'paragraph',
        text:
          'The **"action of the limbs"** is your prayers, your fasting, your character. The scholars agree that *this layer fluctuates*. It rises and falls. This is normal and expected.',
      },

      { type: 'subheading', text: 'Hal — The State' },
      {
        type: 'paragraph',
        text:
          'The emotional feeling you experience. The state, a temporary condition of your heart. You either feel sweetness or you feel dryness. Some days your chest feels softer, and it aches with love while you pray, read the Quran, and some days you feel nothing but emptiness, and every ritual feels mechanical.',
      },
      {
        type: 'paragraph',
        text:
          'So **Hal is what your heart experiences, and it does not define your faith** — that is intact. Your emotional experience of worship is not the same as your faith. The person who prays with tears every day and the person who prays through complete dryness may have the exact same depth of root. The difference is only in the weather above the ground.',
      },
      {
        type: 'quote',
        text:
          'The mistake is treating a state — which is temporary — as a verdict — which is permanent.',
        cite: 'Imam Ibn al-Qayyim al-Jawziyyah',
      },
      {
        type: 'paragraph',
        text:
          'Ibn Qayyim, one of the most psychologically perceptive scholars of Islam, has called this state **"Fatra"** (فترة).',
      },

      { type: 'heading', text: 'Fatra — A Period of Slackening' },
      {
        type: 'paragraph',
        text:
          'A period of spiritual flatness or stillness between two states of nearness. **Not a sign of failure** — a recognized and named part of the spiritual journey.',
      },
      {
        type: 'paragraph',
        text:
          'He says next: *even the Prophets experienced fatra.*',
      },
      {
        type: 'paragraph',
        text:
          'Between the first revelation to the Prophet (SAW) in the Cave of Hira and the next, there was a period of silence. The Prophet (SAW) was distressed. He longed deeply for the return of what he had experienced. He felt the absence acutely.',
      },
      {
        type: 'paragraph',
        text:
          'That gap was not abandonment. **It was a space.** And what came after it was deeper and more expansive than what came before.',
      },

      { type: 'heading', text: 'What is the significance of Fatra?' },
      {
        type: 'paragraph',
        text:
          'This state of spiritual emptiness teaches you what even a state of deep spiritual sweetness cannot. It teaches you **humility**; that in the state of enlightenment, your heart can start feeling that it has earned this sweetness of faith, that you have achieved something and the journey is completed.',
      },
      {
        type: 'paragraph',
        text:
          'And then when you are struck with emptiness inside, you are humbled and reminded that all the sweetness and softness you felt in your worship before was a *blessing of Allah*, not your achievement. So "Fatra" humbles you and reminds you that the journey of refining the state of your heart is not linear — it goes up and down and is a constant struggle in the way to please Allah.',
      },
      {
        type: 'paragraph',
        text:
          'Fatra also creates **longing** in your heart. You wish to feel that fullness of worship again. You wanna cry again in front of your Lord.',
      },
      {
        type: 'paragraph',
        text:
          'Now this core longing to feel the sweetness of Imaan again, even when your heart feels empty, is a pure blessing from Allah. **SubhanAllah!**',
      },

      { type: 'heading', text: 'The Heart Has Seasons' },
      {
        type: 'subheading',
        text: 'From the teachings of Imam Ali (RA)',
      },
      {
        type: 'paragraph',
        text:
          'Do not judge the journey by one valley. The tradition attributed to **Imam Ali (RA)** speaks of the heart moving through cycles of *"iqbal"* (turning toward Allah) and *"idbar"* (turning away from Allah). Not as sin, but as the natural rhythm of the human heart.',
      },
      {
        type: 'quote',
        text:
          'Catch the heart in its moments of iqbal, and give it extra. Pile on the good deeds. Let it run in those open moments. And in the moments of idbar, do not punish yourself. Do not spiral. Do not make the valley deeper by standing in it and despairing. Just maintain the obligations. Hold your ground. The heart will turn again.',
        cite: 'Imam Ali (RA)',
      },
      {
        type: 'paragraph',
        text:
          'He is reported to have said that **the wise person is one who does not judge the whole journey by one valley.**',
      },

      { type: 'heading', text: 'Imagine your Iman as a great old tree' },
      {
        type: 'paragraph',
        text:
          'The **roots** are your belief, your *tasdiq*, your love for Allah, and they go deep into the earth. They do not change with the weather above.',
      },
      {
        type: 'paragraph',
        text:
          'The **branches and leaves** are your *Ahwal*, your state of the heart, your feelings — they move with every wind. Some seasons are full and green, and other seasons are bare and still, and none of these states affects the depth of your faith.',
      },
      {
        type: 'paragraph',
        text:
          'Someone who mistakes bare branches for dead roots will panic. They will grieve what is not lost. They will doubt what has not been left.',
      },
      {
        type: 'paragraph',
        text:
          'But someone who understands the difference watches the bare branches with patience and **keeps watering the roots anyway**.',
      },
      {
        type: 'paragraph',
        text:
          'All of this teaches us that we are humans, and no human emotion is constant. It comes in leaps, and it changes on different days. *And it\u2019s okay to feel empty while praying on some days.*',
      },

      { type: 'heading', text: 'So, what to do to overcome it?' },
      { type: 'subheading', text: 'Practical Guidance for Spiritual Emptiness' },
      {
        type: 'quote',
        text: 'Do not chase the feeling, continue the form.',
        cite: 'Ibn al-Qayyim',
      },
      {
        type: 'paragraph',
        text:
          'This is a profound reorientation. Most of us, when we feel empty in salah, either push harder emotionally — which produces exhaustion — or pull back from worship — which deepens the distance.',
      },
      {
        type: 'paragraph',
        text:
          'Ibn al-Qayyim says, *"Do neither. Just continue. Show up. Pray the prayer. Read the Quran even when the words feel dry. Make the dua even when it feels like nothing is reaching."*',
      },
      {
        type: 'paragraph',
        text:
          'Why? Because he says **the act itself carries barakah the heart has not yet perceived**. The heart is not always the most sensitive instrument. Sometimes the effects of worship are working in the soul long before the feeling registers them. *The feeling follows the action, not the other way around.*',
      },
      {
        type: 'paragraph',
        text:
          'And alongside that, **bring what you are carrying honestly before Allah**. Not a polished or composed dua. The raw thing. The confusion, the dryness, the ache itself. Ghazali says Allah is not distant from the heavy heart. He is most near to the heart that comes to Him in its truest state, not its most composed one.',
      },
      {
        type: 'paragraph',
        text:
          'The very fact that this question keeps you awake, that the emptiness during worship bothers you, that you notice the gap — is called **"hayat al-qalb"**, the aliveness of the heart. *A dead heart does not ask these questions.*',
      },

      { type: 'divider' },
      {
        type: 'paragraph',
        text:
          '**Keep watering the roots, even in the bare seasons.** The rain comes from Him. Your job is only to remain open to the sky. 🤍',
      },
    ],
  },

  {
    id: 'what-is-tawakkul',
    title: 'What is Tawakkul?',
    subtitle: "The Heart's Anchor in Times of Uncertainty",
    author: 'Abdullah Sattar',
    date: '2024-10-17',
    readTime: 5,
    excerpt:
      'Tawakkul is not about letting go of effort — it\'s about letting go of worry. A practical guide to true reliance on Allah, drawn from the Quran and the example of the Prophet ﷺ.',
    coverGradient: 'linear-gradient(135deg, #b68d40 0%, #e1a140 50%, #fefdd3 100%)',
    coverIcon: '🕊️',
    tags: ['Tawakkul', 'Trust', 'Quran'],
    content: [
      {
        type: 'paragraph',
        text:
          '**Tawakkul is not about letting go of effort — it\u2019s about letting go of worry.**',
      },
      {
        type: 'paragraph',
        text:
          'Remember the hadith where a man asked the Prophet (PBUH) if he should tie his camel or just trust in Allah? The Prophet (PBUH) replied:',
      },
      {
        type: 'hadith',
        text: 'Tie it and then rely upon Allah.',
        cite: 'Jami` at-Tirmidhi, 2517',
      },
      {
        type: 'paragraph',
        text:
          'This is a beautiful balance. **Do what you can, and then leave the rest to Allah.** When you feel overwhelmed, remind yourself that worrying will not change anything. What changes things is your effort, your prayer, and your trust in Allah\u2019s wisdom.',
      },

      { type: 'heading', text: 'Quranic Reminder' },
      {
        type: 'paragraph',
        text: 'Allah says:',
      },
      {
        type: 'verse',
        text: 'And whoever puts their trust in Allah, He will be enough for them.',
        cite: 'Surah At-Talaq, 65:3',
      },
      {
        type: 'paragraph',
        text:
          'These words aren\u2019t just meant to be read — *they\u2019re meant to be felt*. When you lean on Allah, you realize that **He truly is enough**.',
      },

      { type: 'heading', text: 'Hadith Wisdom' },
      {
        type: 'paragraph',
        text: 'The Prophet (PBUH) said:',
      },
      {
        type: 'hadith',
        text:
          'If you were to rely upon Allah with true reliance, you would be given provision like the birds: they go out hungry in the morning and return full in the evening.',
        cite: 'Sunan Ibn Majah, 4164',
      },

      { type: 'heading', text: 'A Reminder for Tough Times' },
      {
        type: 'paragraph',
        text: 'Allah says:',
      },
      {
        type: 'verse',
        text: 'Allah does not burden a soul beyond that it can bear.',
        cite: 'Surah Al-Baqarah, 2:286',
      },
      {
        type: 'paragraph',
        text:
          'If you\u2019re struggling, **it\u2019s not because you\u2019re weak or lacking faith**. It\u2019s because you are strong enough to bear this trial, and Allah knows it. Tawakkul is reminding yourself that *the One who brought you to this challenge will also see you through it*.',
      },

      { type: 'heading', text: 'The Comfort in Dua' },
      {
        type: 'paragraph',
        text: 'Allah says:',
      },
      {
        type: 'verse',
        text: 'Call upon Me; I will respond to you.',
        cite: 'Surah Ghafir, 40:60',
      },
      {
        type: 'paragraph',
        text:
          'Tawakkul is about believing in that promise — knowing that help will come from places you least expect, and at times you most need it.',
      },

      { type: 'divider' },
      {
        type: 'paragraph',
        text:
          'So, the next time you feel anxious or unsure, take a deep breath, make your effort, and then say:',
      },
      {
        type: 'quote',
        text:
          '**حسبي الله لا إله إلا هو** — "Hasbi Allahu la ilaha illa Huwa." — *Allah is sufficient for me; there is no deity except Him.*',
      },
      {
        type: 'paragraph',
        text:
          'Feel the weight lift as you remember that **you are never alone**, and your trust in Allah is your greatest strength. 🕊️',
      },
    ],
  },

  {
    id: 'the-most-successful-educationist',
    title: 'The Most Successful Educationist',
    subtitle: 'How the Prophet (SAW) achieved an 89% literacy rate in under a decade',
    author: 'Prof. Mahmood Akhtar',
    date: '2024-09-17',
    readTime: 6,
    excerpt:
      'A miracle transformed the entire structure of education, achieving the highest literacy rate in history despite limited resources. The story of how Prophet Muhammad (SAW) became the most successful educationist the world has ever known.',
    coverGradient: 'linear-gradient(135deg, #0b2545 0%, #1a3a5f 50%, #e1a140 100%)',
    coverIcon: '📚',
    tags: ['Education', 'Seerah', 'History'],
    content: [
      {
        type: 'paragraph',
        text:
          '*A miracle transformed the entire structure of education, achieving the highest literacy rate in history, despite limited resources.*',
      },
      {
        type: 'paragraph',
        text:
          'In an era dominated by superpowers like the **Roman and Persian empires**, there emerged a man who truly enlightened the people, lifting them from the darkness of ignorance. He provided them with light and wisdom. That man is none other than **Prophet Muhammad (SAW)**. Despite facing severe restrictions, he persevered and rose above the challenges.',
      },

      { type: 'heading', text: 'The Secret Beginning at Dar-e-Arqam' },
      {
        type: 'paragraph',
        text:
          'In the early days of Makkah, when he was not permitted to teach openly, Prophet Muhammad (SAW) launched a **secret educational mission** at the house of his companion, *Zaid bin Arqam (RA)*, famously known as **Dar-e-Arqam**. Even under these circumstances, he continued to spread knowledge with relentless effort.',
      },
      {
        type: 'paragraph',
        text: 'The Messenger of Allah (SAW) said:',
      },
      {
        type: 'hadith',
        text:
          'Whoever takes a path upon which to obtain knowledge, Allah makes the path to Paradise easy for him.',
        cite: 'Jami` at-Tirmidhi 2646',
      },
      {
        type: 'paragraph',
        text:
          'This hadith emphasizes the **immense importance of knowledge**. It is so significant that Allah Himself assists the seeker and opens the doors of blessings and paradise for them.',
      },
      {
        type: 'paragraph',
        text:
          'During the Prophet\u2019s time, access to knowledge was limited to two groups: **religious elites** like bishops and popes, and the **wealthy or powerful**. These authorities restricted the common people, especially the poor, from acquiring knowledge or even reading sacred books like the Torah and Injeel.',
      },

      { type: 'heading', text: 'A New Era in Madina' },
      {
        type: 'paragraph',
        text:
          'However, after the migration to Madina, Prophet Muhammad (SAW) was able to continue his mission with more freedom. He began by explaining the **benefits of education**, emphasizing its necessity and usefulness to his companions.',
      },
      {
        type: 'paragraph',
        text:
          'Gradually, more companions realized the importance of knowledge and took it seriously, integrating learning into their daily lives.',
      },
      {
        type: 'paragraph',
        text:
          'For example, one companion, *Jabir Bin Abdullah (RA)*, once **traveled for an entire month** to meet another companion, *Abdullah Bin Unais (RA)*, just to verify the details of a single hadith.',
      },
      {
        type: 'paragraph',
        text:
          'Prophet Muhammad (SAW) further highlighted the value of education when he said:',
      },
      {
        type: 'hadith',
        text:
          'The most superior among you (Muslims) are those who learn the Qur\u2019an and teach it.',
        cite: 'Sahih al-Bukhari 5028',
      },

      { type: 'heading', text: 'From Slaves to Scholars' },
      {
        type: 'paragraph',
        text:
          'Through his tireless efforts, the Prophet (SAW) transformed even slaves into scholars. A shining example is *Sulaiman Bin Yasar (RA)*, a former slave of Memona (RA), who later became **one of the seven top scholars of Madina**. Many companions, including *Yahya Bin Saeed (RA)*, studied hadiths under him.',
      },
      {
        type: 'paragraph',
        text:
          'In Madina, the Prophet (SAW) initiated both **formal and informal education**. Those who attended full-time were part of *Ashab-e-Suffa*, the first group of 70 students. Meanwhile, those engaged in business or work received informal education from the Prophet (SAW).',
      },

      { type: 'heading', text: 'An 89% Literacy Rate — In Under a Decade' },
      {
        type: 'paragraph',
        text:
          'Through continuous day and night efforts, Prophet Muhammad (SAW) achieved a staggering **literacy rate of 89% in less than 10 years** — a feat that remains unmatched to this day. What\u2019s even more remarkable is the *rigorous standard of literacy* he set.',
      },
      {
        type: 'paragraph',
        text:
          'To be considered literate, a person had to understand the difference between:',
      },
      {
        type: 'list',
        items: [
          '**halal and haram**',
          '**permissible and prohibited**',
          '**clean and unclean**',
        ],
      },
      {
        type: 'paragraph',
        text:
          'In comparison, today\u2019s global literacy rates, though higher in percentage, *fall far short of the profound understanding required by the Prophet (SAW).*',
      },

      { type: 'heading', text: 'Educating Women & Sending Out Teachers' },
      {
        type: 'paragraph',
        text:
          'Moreover, the Prophet (SAW) appointed his wife, **Ayesha (RA)**, as the primary educator for the women of Madina. She would hold sessions on *Surah Noor* and *Surah Nisa*, teaching Muslim women the lessons within and helping them address their personal issues. Additionally, the Prophet (SAW) sent companions on educational and preaching missions to regions in need of learning.',
      },
      {
        type: 'paragraph',
        text:
          'It is truly a miracle that Prophet Muhammad (SAW) laid the foundation of an education system that **remains unrivaled**. He achieved an 89% literacy rate in under a decade, despite a lack of resources and a demanding literacy standard.',
      },

      { type: 'heading', text: 'A Legacy That Outlives Lifetimes' },
      {
        type: 'paragraph',
        text: 'The Messenger of Allah (SAW) said:',
      },
      {
        type: 'hadith',
        text:
          'When a person dies, his deeds are cut off except for three: a continuing charity, knowledge from which others benefit, or a righteous child who prays for him.',
        cite: 'Jami` at-Tirmidhi 1376',
      },
      {
        type: 'paragraph',
        text:
          'This statement beautifully encapsulates the **lasting impact of knowledge**. Prophet Muhammad (SAW) left behind a legacy of education that continues to benefit the world, *generation after generation*.',
      },
    ],
  },

  {
    id: 'womens-empowerment-in-islam',
    title: "Women's Empowerment In Islam",
    subtitle: 'Rights, dignity, and equality — granted over 1400 years ago',
    author: 'Abdullah Sattar',
    date: '2024-09-15',
    readTime: 7,
    excerpt:
      'Islam was revolutionary in the way it empowered women, long before modern concepts of gender equality took root. From the right to inherit and own property to the right to education and choice in marriage — explore the deeply rooted foundations of women\'s empowerment in Islam.',
    coverGradient: 'linear-gradient(135deg, #e1a140 0%, #b68d40 50%, #0b2545 100%)',
    coverIcon: '🌸',
    tags: ['Women', 'Rights', 'Equality'],
    content: [
      {
        type: 'paragraph',
        text:
          'Islam is a religion that values **justice, fairness, and equality**. It does not place men above women or women above men; instead, it emphasizes that both are important, respected, and have their own roles in society. Islam was *revolutionary* in the way it empowered women, long before modern concepts of gender equality took root. Let\u2019s explore the foundations of women\u2019s empowerment in Islam.',
      },

      { type: 'heading', text: '1. Women Are Equal to Men' },
      {
        type: 'paragraph',
        text:
          'Islam clearly states that men and women are **equal in their spiritual worth and in their responsibilities**. Both are accountable for their actions and will be rewarded or punished according to their deeds. The Quran emphasizes this equality by stating:',
      },
      {
        type: 'verse',
        text:
          'Whoever does righteous deeds, whether male or female, while being a believer — those will enter Paradise, and they will not be wronged, even as much as the speck on a date seed.',
        cite: 'Quran 4:124',
      },
      {
        type: 'paragraph',
        text:
          'This verse shows that **both men and women are deserving of respect and dignity**. Their worth is measured by their faith and good actions, not their gender. Islam recognizes that while men and women may have different roles at times, *neither is inferior to the other*. This equality was a significant step in elevating the status of women, especially in societies where they were often seen as less important than men.',
      },

      { type: 'heading', text: "2. Women's Rights in Islam" },
      {
        type: 'paragraph',
        text:
          'Islam provided women with rights at a time when many other cultures denied them basic freedoms. Women in Islam have the right to:',
      },
      {
        type: 'list',
        items: [
          '**Seek education**',
          '**Inherit property**',
          '**Engage in business**',
          '**Choose their spouse**',
        ],
      },
      {
        type: 'paragraph',
        text:
          'These rights are fundamental and were granted **over 1400 years ago**, showing that Islam was *ahead of its time* in ensuring justice for women.',
      },
      {
        type: 'paragraph',
        text:
          'The Prophet Muhammad (peace be upon him) said:',
      },
      {
        type: 'hadith',
        text: 'Seeking knowledge is an obligation upon every Muslim.',
        cite: 'Sunan Ibn Majah 224',
      },
      {
        type: 'paragraph',
        text:
          'This hadith does not distinguish between men and women; **both are encouraged to pursue knowledge and improve their lives through education**. In Islamic history, women were scholars, doctors, and businesswomen, actively participating in the growth of their communities.',
      },
      {
        type: 'paragraph',
        text:
          'Moreover, Islam ensures that women have the right to **financial independence**. They can inherit and own property — a right that many women in other cultures were only granted in recent times. Allah says in the Quran:',
      },
      {
        type: 'verse',
        text:
          'For men is a share of what the parents and close relatives leave, and for women is a share of what the parents and close relatives leave, be it little or much — an obligatory share.',
        cite: 'Quran 4:7',
      },
      {
        type: 'paragraph',
        text:
          'This verse highlights that **women have the right to inherit and manage their own wealth**. They are not dependent on their male relatives or husbands for financial security, but are given the freedom to make decisions regarding their own property.',
      },
      {
        type: 'paragraph',
        text:
          'Forced marriages, which were common in many pre-Islamic societies, are **forbidden in Islam**. *A woman has the right to accept or reject a marriage proposal.* The Prophet Muhammad (peace be upon him) said:',
      },
      {
        type: 'hadith',
        text:
          'A woman without a husband has more right to her person than her guardian, and a virgin\u2019s consent must be asked from her, and her silence implies her consent.',
        cite: 'Sahih Muslim 1421',
      },

      {
        type: 'heading',
        text: '3. Islam: The First Religion to Give Women Values',
      },
      {
        type: 'paragraph',
        text:
          'Islam was one of the **first religions to recognize the value and worth of women**, empowering them to be active members of society. The teachings of Islam encourage women to seek education, work, and contribute to the welfare of their communities. *Women\u2019s empowerment in Islam is not limited to certain areas of life*; rather, they are given the opportunity to fulfill their potential in all aspects.',
      },
      {
        type: 'paragraph',
        text:
          'The Prophet Muhammad (peace be upon him) was known for his **kindness and respect toward women**. He taught his followers to treat women with honor, saying:',
      },
      {
        type: 'hadith',
        text: 'The best of you are those who are best to their women.',
        cite: 'Jami` at-Tirmidhi 1162',
      },

      { type: 'heading', text: 'Women Who Shaped Islamic History' },
      {
        type: 'paragraph',
        text:
          'Throughout Muslim history, there are many examples of women who excelled in various fields. **Aisha bint Abu Bakr**, the wife of the Prophet, was a great scholar whose knowledge of hadith and Islamic jurisprudence is still studied today. She taught numerous companions of the Prophet, and her wisdom continues to benefit Muslims worldwide. Similarly, **Khadijah**, the Prophet\u2019s first wife, was a successful businesswoman who supported him in his mission.',
      },
      {
        type: 'paragraph',
        text:
          'During the Islamic Golden Age, we see more examples of women excelling in knowledge and contributing to society. **Fatima al-Fihri**, for instance, founded the *University of al-Qarawiyyin in Morocco* — the **oldest continually operating university in the world**. It remains a center for learning today, showcasing the lasting impact of a Muslim woman\u2019s contribution to education.',
      },
      {
        type: 'paragraph',
        text:
          'Another notable figure is **Lubna of Cordoba**, a 10th-century scholar in *Al-Andalus (Muslim Spain)*. She was renowned for her intellect and mastery of mathematics, literature, and poetry. Her role as a scribe and librarian in the Caliph\u2019s court shows that women were involved in intellectual and cultural advancements during the Islamic Golden Age.',
      },
      {
        type: 'paragraph',
        text:
          'These women, and many more, played **vital roles in shaping the intellectual and social landscape of their time**. Islam encouraged and supported their efforts, recognizing their contributions as essential to the progress of society.',
      },

      { type: 'divider' },
      {
        type: 'paragraph',
        text:
          'Islam lays down a **strong foundation for the empowerment of women** by ensuring their rights, providing them with opportunities for education, and protecting their dignity. The teachings of the Quran and the example of the Prophet Muhammad (peace be upon him) make it clear that women are to be treated with *respect, love, and honor*. By understanding and applying these principles, Muslims today can continue to uphold the high status that Islam gives to women.',
      },
      {
        type: 'paragraph',
        text:
          '**Empowerment in Islam is not a modern trend** but a deeply rooted value that has been present since the time of revelation. By looking back at Islamic teachings and history, we see a message of *equality, justice, and respect for all*, regardless of gender. 🌸',
      },
    ],
  },
    {
      id: 'the-true-history-of-karbala',
      title: 'The True History of Karbala',
      subtitle: 'We say we love the family of the Prophet PBUH — but do we know them?',
      author: 'Dr. Samaviya Alamgir',
      date: '2026-06-27',
      readTime: 19,
      excerpt: 'Do we feel their sacrifices. Their tears and their patience. The ground they bled on. The children they saw getting martyred. The injustice they endured while calling the world back to truth, back to Allah. Back to the message of the Prophet peace be upon him…',
      coverGradient: 'linear-gradient(135deg, #1a3a5f 0%, #b68d40 50%, #e1a140 100%)',
      coverIcon: '🌙',
      tags: ['Karbala', 'Ahlul Bayt', 'History', 'Muharram', 'Sahabah'],
      content: [
        {
          type: 'paragraph',
          text: '**We say we love the family of the Prophet peace be upon him, but do we know them?**',
        },
        {
          type: 'paragraph',
          text: 'Do we feel their sacrifices. Their tears and their patience. The ground they bled on. The children they saw getting martyred. The injustice they endured while calling the world back to truth, back to Allah. Back to the message of the Prophet peace be upon him Muharram is here, and in our generation, something has shifted. Once there was a time when it was considered disrespectful to even smile on the days of Muharram. Now we play music and make holiday plans for these days, and the days pass like any other day. There was a time when the companions of the Prophet, the scholars, and the believers would weep. Because they knew what happened. They understood what was lost at Karbala. They felt the weight of it. Knowledge is the beginning of love. And love without knowledge is shallow. I wanted to write about the incident of Karbala, wanted to learn about it, feel it, and share with you all because to love the Prophet peace be upon him, is to love his family. And to love his family is to know their story. Narrating from the words of scholars, narrators, and historians like Ibn Kathir, al-Tabari, and Ibn al-Athir. Imam Hussain did not stand up for the throne. He stood up because falsehood had taken power, and silence would have been betrayal of his grandfather\'s message, of the Ummah, of Allah Himself. It was the most courageous act to fight for the right in the way of Allah.',
        },
        {
          type: 'heading',
          text: 'The Status of the Prophet\u2019s Family & the Unity of the Companions',
        },
        {
          type: 'paragraph',
          text: 'Before we speak of Karbala, we must establish something important, something that the noise of sectarianism has blurred. The family of the Prophet peace be upon him, the **Ahl al-Bayt**, holds a rank in Islam that no words can fully capture. The Prophet peace be upon him himself said:',
        },
        {
          type: 'hadith',
          text: 'I am leaving among you two weighty things: the Book of Allah, in which there is right guidance and light, so hold fast to the Book of Allah and adhere to it. And the members of my household — I remind you of Allah regarding the members of my household. I remind you of Allah regarding the members of my household. I remind you of Allah regarding the members of my household.',
          cite: 'Sahih Muslim 2408a',
        },
        {
          type: 'paragraph',
          text: 'He called them his **two weighty things** alongside the Quran. He covered them under his cloak and prayed for them. And about his daughter Fatimah ‫رضي الله عنها‬, he said:',
        },
        {
          type: 'hadith',
          text: 'Fatimah is a part of me, and whoever makes her angry, makes me angry.',
          cite: 'Sahih al-Bukhari 3714',
        },
        {
          type: 'paragraph',
          text: 'Ali ibn Abi Talib ‫رضي الله عنه‬ was the cousin of the Prophet peace be upon him, his son-in-law, the father of Hasan and Hussain, and one of the most beloved companions. But here is what must also be said and said clearly: Even the closest companions of the Prophet peace be upon him, knew the respect and highest status of Ahlul Bayt. Hazrat Abu Bakr al-Siddiq ‫رضي الله عنه‬, the closest companion of the Prophet peace be upon him, loved the family of the Prophet deeply. He said: *"Show reverence to Muhammad peace be upon him by honoring the members of his family."* (Sahih al-Bukhari 3713)',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Umar sought closeness to the Prophet\'s family through marriage; he married Hazrat Umm Kulthum, the daughter of Hazrat Ali and Hazrat Fatimah, an act of deep respect and love. Hazrat Ali named some of his own sons Abu Bakr, Umar, and Uthman, after his brothers in Islam. This is documented in classical historical sources, including Ibn al-Athir\'s *al-Kamil fi al-Tarikh*. These were not enemies. They were brothers who sometimes disagreed as humans do, but who loved the Prophet peace be upon him and his legacy. To speak of Karbala authentically, we must hold this truth: the companions were human beings of immense virtue. Disagreements happened between them because they were humans. But we are obligated to respect every one of them, as they were the best of the best humans by Allah. They were loved and were brothers of Our Prophet peace be upon him, stood by his side and fought and bled and were martyred in the name of Allah, the status of everyone of those is inarguably unmatched. The tragedy of Karbala was not born from the companions, it came from a generation that had drifted far from their spirit. And the wives of the Prophet peace be upon him, the *Ummahat al-Mu\'minin*, the Mothers of the Believers, they are also Ahl al-Bayt in honor and in station. Love them all. Honor them all. And learn history with a heart free of hatred.',
        },
        {
          type: 'heading',
          text: 'The Political World After the Prophet PBUH',
        },
        {
          type: 'paragraph',
          text: 'To understand Karbala, you must understand what the Muslim world looked like in the decades before it. The Prophet peace be upon him passed from this world in 11 AH / 632 CE. The community he left behind was young, vast, and rapidly expanding. Within two decades, the Muslim empire stretched from Persia to Egypt. With that expansion came something the Prophet peace be upon him had warned about: the temptations of power, wealth, and worldly leadership. The first three caliphs of Islam, Hazrat Abu Bakr, Hazrat Umar, and Hazrat Uthman (May Allah be pleased with them) ruled with varying strengths and faced immense challenges. By the caliphate of Hazrat Uthman, internal political tensions had grown. Accusations of nepotism, powerful tribal factions, and regional governors with growing ambitions created fractures in the ummah.',
        },
        {
          type: 'paragraph',
          text: 'In 35 AH/ 656 CE Hazrat Uthman was assassinated, one of the most catastrophic, heartbreaking event that shook the whole Muslim Hazrat Ali became the fourth Caliph. His caliphate was marked by civil wars, the Battle of the Camel, and the Battle of Siffin. Muslims were fighting Muslims. It was a wound that never fully healed.',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Muawiyah ibn Abi Sufyan, the governor of Syria, refused to pledge allegiance to Hazrat Ali until Hazrat Uthman\'s killers were brought to justice. Now make sure to remember Hazrat Muawaiyah was also one of the most senior and beloved companions of Prophet peace be upon him, we are absolutely not allowed to disrespect him, his stance was to fight against the murderers who martyred Hazrat Uthman painfully and he wanted justice, while Hazrat Ali knew that the internal stability of Muslim ummah and state was most crucial at that point or else muslim world would\'ve fallen apart from external forces. This political standoff led to years of conflict. And led to the martyrdom of Hazrat Ali by Khawarji\'ites, the extremists, who used Islam just for the gain of power.',
        },
        {
          type: 'paragraph',
          text: 'After the martyrdom of Hazrat Ali ‫رضي الله عنه‬ in 40 AH, his eldest son Hazrat Hasan became the caliph. In an act of extraordinary sacrifice for the unity of the ummah, Hazrat Hasan abdicated and made peace with Hazrat Muawiyah on the condition that Hazrat Muawiyah would not appoint a successor but would leave the choice to the Muslims. The Prophet peace be upon him had said about Hasan, years before this moment ever came:',
        },
        {
          type: 'hadith',
          text: 'This son of mine is a Sayyid (a noble leader), and perhaps Allah will bring about reconciliation between two great groups of the Muslims through him.',
          cite: 'Sahih al-Bukhari 2704',
        },
        {
          type: 'paragraph',
          text: 'That peace held. But then, around the time of his departure, too many wars had happened. Can you imagine that more Muslims were martyred in these internal wars than were in *Ghazwas*, the battles against the non-believers, the Romans, and others. Due to this immense bloodshed and to avoid it in the future, Hazrat Muawiyah broke the most critical condition of that treaty. He appointed his own son Yazid as his successor.',
        },
        {
          type: 'heading',
          text: 'Who was Yazid?',
        },
        {
          type: 'paragraph',
          text: 'When Hazrat Muawiyah ibn Abi Sufyan ‫ رضي هللا عنه‬appointed his son Yazid as successor, bypassing the consultative tradition the early caliphs had established or what Islam has taught, many of the most respected companions and their children were deeply troubled. Again, we can disagree with his political decision, we are allowed to do so BUT never in any circumstances we can question the intentions of the beloved companion of the Prophet peace be upon him who fought for him for Allah and sacrificed in His way.',
        },
        {
          type: 'paragraph',
          text: 'Now Yazid ibn Muawiyah was known, even among Muslim historians who are not partisan, as someone whose character fell far short of what the leadership of the Prophet\'s ummah demanded. Ibn Kathir, one of the most authoritative Sunni historians, writes about Yazid in al-Bidayah wa al-Nihaya. The scholars of Ahlus Sunnah have generally held that Yazid\'s rule began with illegitimacy and included grave moral failures. He was known for his love of wine, his disregard for Islamic norms, and his cruel appointments. He turned haram to halal, and His governor in Iraq, Ubaydullah ibn Ziyad, and later his governor in Medina, al-Walid ibn Utbah, were instruments of his repression.',
        },
        {
          type: 'paragraph',
          text: 'When Hazrat Muawiyah passed in 60 AH / 680 CE, and Yazid took power, his first order was clear: Get the pledge of allegiance from everyone. Especially Hazrat Hussain ibn Ali. Because HazratHussain ‫رضي هللا عنه‬, the grandson of the Prophet peace be upon him, the son of Hazrat Ali and Hazrat Fatimah because his pledge would legitimize everything. And Hazrat Hussain refused.',
        },
        {
          type: 'paragraph',
          text: 'He refused because giving bay\'ah, an oath of loyalty to an immoral and cruel ruler like Yazid, would mean telling the ummah: this is acceptable. This is what Islamic leadership looks like. He could not do that. He would not do that. As he himself declared when he left Medina:',
        },
        {
          type: 'quote',
          text: 'I have not risen to cause corruption or show off, but I have risen to seek reform in the ummah of my grandfather, peace be upon him, and I want to enjoin good and forbid evil.',
        },
        {
          type: 'paragraph',
          text: 'He was constantly getting letters from hundreds of them from the tribes of Kufa to come and fight, and they would stand with him and support him against evil.',
        },
        {
          type: 'heading',
          text: 'The People of Kufa: A City of Broken Promises',
        },
        {
          type: 'paragraph',
          text: 'Now Kufa. A city in Iraq that had loved Hazrat Ali ‫ رضي هللا عنه‬and mourned him. A city that had wept when Hazrat Hasan ‫ رضي هللا عنه‬made peace and stepped aside. A city that had always said: "We are with you, O family of the Prophet." When news spread that Yazid had taken power and was demanding allegiance, the people of Kufa erupted. Letters began arriving at the home of Hazrat Hussain ‫ رضي هللا عنه‬in Medina. Hundreds. Then thousands. Historians record that over 12,000 letters were sent. Saying: "Come to us, O son of the Messenger of Allah. We have no imam. We await you. Come and we will support you." They pledged their lives. Their swords and their loyalty. But this was not the first time the people of Kufa had made such promises to the Prophet\'s family. In the time of Hazrat Ali ‫رضي هللا عنه‬, the Kufans had pledged with him and then wavered, betrayed him, dragging their feet in battle, abandoning him at critical moments. Hazrat Ali ‫ رضي هللا عنه‬himself had famously lamented their inconsistency in his sermons recorded in classical sources. When Hazrat Hasan ‫ رضي هللا عنه‬sought their support against Hazrat Muawiyah, they began defecting and even attempted to hand him over. His own camp was fractured. The pain broke Hazrat Hasan ‫ رضي هللا عنه‬so deeply that he negotiated peace rather than lead men who would not truly fight. Hazrat Hussain ‫ رضي هللا عنه‬knew this history.',
        },
        {
          type: 'paragraph',
          text: 'His advisors, including his half-brother Muhammad ibn al-Hanafiyyah, and even Hazrat Abdullah ibn Abbas ‫رضي هللا عنه‬, one of the greatest companions and the most senior and experienced Sahabi alive at that time, begged him not to trust the Kufans. "They will abandon you as they abandoned your father and your brother." They warned. Hazrat Hussain ‫ رضي هللا عنه‬listened, and he reflected. And then he made a decision that history would never forget. He sent his cousin first to confirm. His name was Muslim ibn Aqil.',
        },
        {
          type: 'heading',
          text: 'Muslim Ibn Aqil: The Fearless First Martyr',
        },
        {
          type: 'paragraph',
          text: 'His name was Muslim ibn Aqil ‫رضي هللا عنه‬, the cousin of Hazrat Hussain, his most trusted companion, a man of courage and faith. Imam Hussain sent him to Kufa to assess the situation. To verify: were these people serious? Was their support real? When Hazrat Muslim arrived in Kufa, the city embraced him. People flooded to pledge allegiance. Thousands registered their names. The energy was electric. He wrote back to Hazrat Hussain:',
        },
        {
          type: 'paragraph',
          text: '"The people of Kufa are with you. Come. They will stand with you.” But within weeks, everything collapsed. Yazid\'s feared governor, Ubaydullah ibn Ziyad, arrived in Kufa. He was ruthless, calculating, and terrifying. He arrested tribal leaders, threatened families, bribed others, and the crowds melted away one by one, then in thousands. Hazrat Muslim ibn Aqil found himself abandoned. The man whose house he had used as a base, Hani ibn Urwah, was arrested and beaten publicly. Hazrat Muslim was alone now, with no army, no safe house, and no allies willing to show their face, he still tried to resist. He was martyred fighting. He was dragged before Ibn Ziyad and executed, and his body was crucified and thrown from the palace wall. Astaghfirullah Ibn Kathir and al-Tabari both record that before his execution, Hazrat Muslim\'s final request was that someone send word to Hussain to warn him. To tell him not to come. That message could not reach Hazrat Hussain in time.',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Muslim ibn Aqil ‫ رضي هللا عنه‬was the first martyr of Karbala, before a single battle was fought in the desert.',
        },
        {
          type: 'heading',
          text: 'Departure of Imam Hussain from Mecca',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Hussain ‫ رضي هللا عنه‬was in Mecca.',
        },
        {
          type: 'paragraph',
          text: 'He had left Medina, the city of his grandfather, peace be upon him, after refusing to pledge allegiance to Yazid. He could not stay in Medina, where the governor would be compelled to arrest or kill him. Mecca, the sacred sanctuary. The house of Allah. He took shelter there with his family. Even Hazrat Hussain\'s enemies would not dare to violate its sanctity. Or so it seemed. Then came the warning, Yazid had sent assassins dressed as pilgrims, with knives hidden beneath their ihram, with orders to kill Hazrat Hussain ‫ رضي هللا عنه‬even if it was during Hajj, even within the precincts of the Ka\'bah. Astaghfirullah Hazrat Hussain ‫ رضي هللا عنه‬would not allow the blood to be shed to desecrate the House of God. He could not bear to even think of the sanctity of the house of Allah being destroyed by bloodshed. So, on the 8th of Dhul Hijjah, the day pilgrims begin the rites of Hajj, Hazrat Hussain ‫رضي هللا عنه‬ made a decision that shocked everyone around him. He left Mecca. He left Hajj incomplete. He turned toward Iraq. His companions, his well-wishers, pleaded with him not to go. Hazrat Abdullah ibn Abbas ‫رضي هللا‬ ‫عنه‬, deeply learned and experienced, warned him with tears in his eyes about the treachery of the Kufans. He told him that Prophet peace be upon him had turned away the dunya and chosen Akhirah for himself and his family from Allah. He told him that the Prophet’s family was never meant to rule or attain power in this dunya. They have the power, they will be most powerful in the Akhirah, they will be leaders of the people of Paradise. But Hazrat Hussain never wanted the power. His goal was clear: “To stand against the evil, the fitnah, and fight in the way of Allah, to stand against injustice, against oppression” Hazrat Hussain ‫ رضي هللا عنه‬answered him gently but firmly. He did not have the force to conquer; he traveled with his family, his sisters, his children, his nephews, the women and elderly of his household, a caravan of truth moving toward a destiny already written. Along the way, a poet named al-Farazdaq crossed paths with him and told him the people of Kufa were many, but their swords were with the ruler and their hearts with Hussain. Hazrat Hussain ‫ رضي هللا عنه‬replied: "You have spoken the truth. All affairs are in Allah\'s hands. He does whatever He wills." He continued walking.',
        },
        {
          type: 'heading',
          text: 'The Companions of Karbala',
        },
        {
          type: 'paragraph',
          text: 'Who came with Hazrat Hussain ‫ رضي هللا عنه‬to Karbala? His own family. His brothers, Hazrat Abbas, Hazrat Ali al-Akbar, Ja\'far, Uthman, and others, sons of Hazrat Ali ibn Abi Talib from different mothers, came. They did not have to, but they chose to. They would not let their brother face death alone. His sons. His nephews. Cousins from the family of Hazrat Aqil ibn Abi Talib. Sons of Hazrat Ja\'far ibn Abi Talib. Young men who had entire lives ahead of them. And a small group of loyal companions, men who had lived in the era of the companions, who knew what this family of Ahlul Bayt meant, who could not live with themselves if they did not stand.',
        },
        {
          type: 'paragraph',
          text: 'One of them was Hazrat Zuhayr ibn Qayn, who had initially been reluctant to even meet Hazrat Hussain ‫ رضي هللا عنه‬on the road. After a brief conversation with him, he returned to his caravan, said farewell to his wife, and joined Hazrat Hussain forever. By the time they reached the plains of Karbala on the 2nd of Muharram, 61 AH, the total number of Hussain\'s camp was approximately 72 fighters, men who chose conscience over survival. Facing them was an army that historians estimate at thousands. Some narrations place it at 30,000. 72 against thousands. And yet in this moment Hazrat Hussain ‫ رضي هللا عنه‬gathered his companions on the night before the battle and said something remarkable: "These people want only me. I release you all from your pledge. Take my family members and leave under cover of darkness. None of them has enmity toward you." He turned off the lanterns so no one would feel shame. But when the lamps were turned back on, every single one of his companions was there, and no one left. And one by one, his companions said: "By Allah, we will not leave you."',
        },
        {
          type: 'heading',
          text: 'In Karbala: The Days before the Battle',
        },
        {
          type: 'paragraph',
          text: 'The plain of Karbala was dry with scorching heat. The Euphrates river was nearby but soon, cruelly, cut off. Hazrat Hussain ‫ رضي هللا عنه‬and his caravan were intercepted by the advance force of ibn Ziyad\'s army led first by Hurr ibn Yazid al-Riyahi, a commander who blocked their path but treated them with some dignity. For days, the camp waited. Hazrat Hussain ‫ رضي هللا عنه‬sent letters to the people of Kufa. He addressed the army directly:',
        },
        {
          type: 'paragraph',
          text: '"Did you not write to me? Did you not invite me? If you now hate my coming, I will return." But ibn Ziyad\'s orders were absolute: Surrender or face the sword. Then came the most heartbreaking cruelty. On the 7th of Muharram, the Euphrates was blockaded. No water was allowed to reach Hazrat Hussain\'s camp, to his family, which had little kids and women. Children cried. Women comforted them with parched lips. The heat of the Iraqi desert beat down. Hazrat Abbas ibn Ali ‫ رضي هللا عنه‬, Hussain\'s brother, called "the Moon of the Hashimites" for his courage and beauty. He led a group of men to break through the blockade and fill water skins. He succeeded. He was returning to the camp when he was ambushed. Enemies cut his right arm, which was holding water for his family. Then he left. At last, He carried the water skin in his teeth and rode back, and then an arrow struck his skin. The water spilled into the desert sand. Hazrat Abbas fell on the ground, and in his last moments, He called out to Hazrat Hussain not to come out. His arms were gone, and he could not bear for his brother to see him like that.',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Hussain rushed to him anyway. He held his brother\'s broken body and He wept. Allah Hu Akbar!',
        },
        {
          type: 'heading',
          text: 'The Day of Ashura: 10th of Muharram',
        },
        {
          type: 'paragraph',
          text: 'The 10th of Muharram. 61 AH. The morning came with a silence heavier than the desert itself. Hazrat Hussain ‫ رضي هللا عنه‬performed Fajr prayer with his companions, 72 men who knew they would not see another dawn. He mounted his horse. He rode toward the opposing army of thousands of men and addressed them: "O people, trace back my lineage and see who I am. Look at who you are about to kill. Is it not shameful for you to shed my blood? Am I not the son of your Prophet\'s daughter? Is there any other son of a prophet among you besides me?"',
        },
        {
          type: 'paragraph',
          text: 'The words hung in the air. Some wept and some turned away. Most stayed. One man did not stay. He was Hurr ibn Yazid al-Riyahi, the commander who had first intercepted Hussain\'s caravan, turned his horse around. He rode toward Hazrat Hussain\'s camp, weeping, his body trembling. "I am the one who stopped you from returning. I did not know this day would come. I repent to Allah."',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Hussain welcomed him. Hurr fought and was martyred that day in Hazrat Hussain\'s defense. He is remembered as the man who chose the right side at the last moment.',
        },
        {
          type: 'paragraph',
          text: 'Then the battle began. One by one, Hazrat Hussain\'s companions fell. The young men of his family, Hazrat Ali al-Akbar, Hazrat Hussain\'s own son, who so resembled the Prophet peace be upon him in appearance that Hazrat Hussain would look at him when he missed his grandfather, who was martyred in front of his eyes',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Hussain buried them with his own hands. Then Hazrat Qasim ibn Hasan, Hazrat Hasan\'s young son, barely a teenager, recently married he fought courageously and was martyred. And still Hazrat Hussain stood fearlessly.',
        },
        {
          type: 'heading',
          text: 'The Last Moments of Imam Hussain ibn Ali',
        },
        {
          type: 'paragraph',
          text: 'By the afternoon of Ashura, the companions of Hussain ‫ رضي هللا عنه‬were gone. The men of his family were gone.',
        },
        {
          type: 'paragraph',
          text: 'He stood alone. Wounded. Thirsty. In a desert soaked with the blood of those he loved. He asked for a moment to pray. Even now, at the end of everything, his last wish was to bow before his Lord. The narrations record that he prayed bleeding, barely able to stand, and in that state, still called out:',
        },
        {
          type: 'paragraph',
          text: '*"O Allah, I have patience upon Your decree. There is no deity worthy of worship but You."* He was surrounded. Arrows came. Spears came. He fell from his horse. He was martyred. The grandson of the Prophet Muhammad peace be upon him — the very one who used to sit on the Prophet\'s back during *sujood* as a child, whom the Prophet peace be upon him would kiss and say:',
        },
        {
          type: 'hadith',
          text: 'Hussain is from me, and I am from Hussain. Allah loves whoever loves Hussain. Hussain is a tribe among the tribes.',
          cite: 'Jami` at-Tirmidhi 3775 (hasan); also Sunan Ibn Majah 144',
        },
        {
          type: 'paragraph',
          text: 'He was martyred in the most brutal manner, by people who called themselves Muslim. His head was separated from his body. His camp was burned. The women and children, including Hazrat Zainab bint Ali ‫رضي الله عنها‬, the sister of Hazrat Hussain and one of the most remarkable women in Islamic history, were taken captive. They were marched to Kufa, then to Damascus, to the court of Yazid. And Hazrat Zainab ‫رضي الله عنها‬, in chains, in grief, in the court of her brother\'s killer, stood up and delivered a sermon of such power and dignity that the entire court fell silent. She said: *"Do you think that by killing us you have disgraced us? By Allah, this is an honor. And your shame is eternal."* The women of the Prophet\'s family, even in captivity, carried the message of Hazrat Hussain forward. They showed an everlasting example of courage and strength for the sake of Allah.',
        },
        {
          type: 'heading',
          text: 'What Karbala Teaches Us?',
        },
        {
          type: 'paragraph',
          text: 'On a plain in Iraq, 72 people chose truth over survival. They were not fighting for power. They were not seeking a throne. They were holding a moral, spiritual line so that Islam would not be redefined by the corrupt and the cruel. Hazrat Hussain ‫ رضي هللا عنه‬did not win the battle of Karbala. But he won something far greater. He won history. He won the hearts of every generation that would come after, Muslim and non-Muslim alike. Mahatma Gandhi wrote about him. Historians across centuries have paused before his story in awe.',
        },
        {
          type: 'paragraph',
          text: 'We take pride and honor being born and Muslims, our hearts bleed and weep for the Qayamah that our Ahlul Bayt went through, the family the beloved people of the Prophet peace be upon him, who bled so that the truth would survive. Honor the family of the Prophet peace be upon him with genuine knowledge of who they were, what they endured, and why it matters. Lower your voices in respect of their sacrifice, remember them in your prayers, remember what they sacrificed for Islam, and send Salawats upon them. Keep fast on this day, and pray to Allah abundantly.',
        },
        {
          type: 'paragraph',
          text: 'May Allah have mercy on Imam Hussain ibn Ali, on his family, on his companions, and on the family of the Prophet peace be upon him. May Allah reunite us with them in Jannah where no horses charge, no arrows fly, and no child cries for water. ‎‫آمين‬',
        },
      ],
    },
    {
      id: 'the-man-who-feared-dying-wealthy',
      title: 'The Man Who Feared Dying Wealthy',
      author: 'Dr. Samaviya Alamgir',
      date: '2026-09-22',
      readTime: 9,
      excerpt: 'A while ago, I got a chance to read the story of Hazrat Khabbab Ibn-Al-Aratt (May Allah be pleased with him), one of the earliest Muslims who accepted Islam. Not many people know about his life.',
      coverGradient: 'linear-gradient(135deg, #0b2545 0%, #b68d40 100%)',
      coverIcon: '✨',
      tags: ['Sahabah', 'Seerah', 'History'],
      content: [
        {
          type: 'paragraph',
          text: 'A while ago, I got a chance to read the story of Hazrat Khabbab Ibn-Al-Aratt (May Allah be pleased with him), one of the earliest Muslims who accepted Islam. Not many people know about his life.',
        },

        { type: 'heading', text: 'The Blacksmith Who Had No One' },
        {
          type: 'paragraph',
          text: 'Before Islam, Hazrat Khabbab was a blacksmith in Makkah. He made swords with his own hands. He had no powerful family, no tribe to stand behind him if things went wrong. He was, in a sense, alone in the world, a slave, just like Hazrat Bilal (May Allah be pleased with him). So when he accepted Islam, he was one of the very first people ever to do so, and there was no one to shield him from what came next.',
        },
        {
          type: 'paragraph',
          text: 'He worked for a woman, a widow named Umm Anmar. When she found out he had become Muslim, something in her turned cruel. She began to torture him herself. She would heat a piece of iron in the fire until it glowed red, then press it onto his head and his back and burn his skin. He would lose consciousness from the pain. And when he woke up, still burning, still hurting, he would say it again. There is no god but Allah (*La ilaha Illa Allah*).',
        },
        {
          type: 'quote',
          text: 'Imagine that. Waking up from that kind of pain, and the first thing on your tongue is still your faith. SubhanAllah.',
        },

        { type: 'heading', text: '"Will You Not Ask Allah to Help Us?"' },
        {
          type: 'paragraph',
          text: 'One day, Hazrat Khabbab couldn\'t take it anymore. He went and found the Prophet (peace be upon him), who was sitting in the shade near the Ka\'bah, his cloak folded beneath him. Khabbab asked him plainly,',
        },
        {
          type: 'quote',
          text: 'Would you seek help for us? Would you pray to Allah for us?',
          cite: 'Sahih al-Bukhari 3612',
        },
        {
          type: 'paragraph',
          text: 'The Prophet (peace be upon him) sat up. His face changed. And what he said next is something Khabbab carried with him for the rest of his life. He told Khabbab about the people who came before them, a believing man placed in a ditch and sawed in two, or combed with iron combs until flesh was stripped from bone, and none of it made them abandon their religion. Then he looked at Khabbab and said,',
        },
        {
          type: 'hadith',
          text: 'By Allah, this religion will prevail till a traveler from San\'a\' to Hadramawt will fear none but Allah, or a wolf as regards his sheep, but you (people) are hasty.',
          cite: 'Sahih al-Bukhari 3612, also 6943',
        },
        {
          type: 'paragraph',
          text: 'By Allah, if we were in his place, we would have given up in an instant, yet he stood fast and strong. The Prophet (peace be upon him) reminded him that his pain will not be wasted, and Allah is All-seeing and All-knowing.',
        },

        { type: 'heading', text: 'Allah Sees Everything' },
        {
          type: 'paragraph',
          text: 'Here\'s the part that people often miss. Even though the Prophet (peace be upon him) told Khabbab to be patient, seerah accounts also describe that he made dua for Khabbab against Umm Anmar\'s cruelty. A short while later, something strange happened to her. Historical reports describe her falling seriously ill, so severe that she began crying out in a way that frightened everyone around her.',
        },
        {
          type: 'paragraph',
          text: 'She went looking for a cure, and someone told her the only way to treat this illness was to burn her head with hot iron, by cauterization. Think about that for a second. The very method she used to torment Khabbab became the only thing that could bring her relief. Some seerah narrations go even further and say that it was Khabbab himself who ended up placing the hot iron on her head, at her own request, as her treatment. The same hands that had been burned by her were now the hands healing her. (These details are from early biographical sources such as Ibn Sa\'d\'s *al-Tabaqat*, not from *Sahih al-Bukhari* or *Sahih Muslim*.)',
        },
        {
          type: 'quote',
          text: 'There\'s no bitterness in this part of the story. No revenge. Just a quiet reminder that Allah sees everything, even in silence, and that the same hands that endured cruelty can still choose mercy when the moment turns.',
        },

        { type: 'heading', text: 'From Nothing to Wealth' },
        {
          type: 'paragraph',
          text: 'After she died from the illness, he was finally able to migrate to Madinah, a place where he could breathe, worship, and live without fear knocking on his door every day. He fought at Badr alongside the Prophet (peace be upon him). He stayed close to him for the rest of his life. And slowly, through honest work and the blessings that came with the growing Muslim community, Khabbab, the man who once had nothing, not even ownership of his own body, became wealthy by the grace and mercy of Allah Almighty.',
        },
        {
          type: 'paragraph',
          text: 'Years later, under the leadership of Hazrat Umar (RA), an important question came up. How should the wealth now flowing into the Muslim treasury be shared among the companions? Hazrat Umar had actually wanted to raise this earlier, during Hazrat Abu Bakr\'s (RA) time as caliph. His idea was simple. The people who accepted Islam first, and who suffered the most for it, should receive more than those who joined later, once it was safer to accept Islam. But Hazrat Abu Bakr said no. His reasoning was rooted in something beautiful. The Prophet (peace be upon him) himself never divided the Sahaba\'s worldly shares this way, and Hazrat Abu Bakr didn\'t want to start a practice the Prophet hadn\'t started. Under Hazrat Abu Bakr, everyone\'s share stayed equal, no ranking, no comparison.',
        },
        {
          type: 'paragraph',
          text: 'When Hazrat Umar became caliph himself, he revisited it. This time, he set up a proper register, the *diwan*, and stipends were given based on how early someone had accepted Islam, how early they had migrated, and whether they\'d fought at Badr. The earlier and harder the sacrifice, the greater the share. Division on the basis of justice and equity.',
        },
        {
          type: 'paragraph',
          text: 'As part of this process, Umar went personally to see Khabbab. He didn\'t just want numbers or reports. He wanted to hear it from Khabbab himself. Classical reports describe Khabbab showing Umar the marks on his back from the polytheists\' torture, scar after scar, burn after burn, a map of every day he had survived. (Ibn Sa\'d, *al-Tabaqat al-Kubra*, also Sunan Ibn Majah 153.)',
        },

        { type: 'heading', text: 'A Door Always Left Open' },
        {
          type: 'paragraph',
          text: 'Despite everything he now owned, Hazrat Khabbab never let it own him. It\'s said that in his house, there was a door that was always left open. Anyone who needed anything could walk in and take it, no need to ask, no need to explain themselves, no need to feel small for needing help. They could literally come and take anything from his home. His generosity wasn\'t something he decided to do occasionally. It was simply how his house was built.',
        },

        { type: 'heading', text: 'The Fear That Wealth Brought' },
        {
          type: 'paragraph',
          text: 'And now, the part of Hazrat Khabbab\'s story that quietly breaks your heart. The more wealth Allah gave him, the more he cried. Because he was afraid. He would sit with his comfort, his house, his money, and his mind would go back to the friends, the companions, the Sahabas, who never got to see any of this. Men who were martyred for their faith before they ever tasted a single day of ease in this world. And a fear would creep into his heart.',
        },
        {
          type: 'quote',
          text: 'What if this comfort is my reward, given to me early, in this life, while theirs is still waiting for them, untouched, in the next?',
          cite: 'Paraphrasing the meaning of Sahih al-Bukhari 4047',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Khabbab himself explained this fear in his own words, preserved in the two *Sahihs*.',
        },
        {
          type: 'hadith',
          text: 'We migrated in the company of Allah\'s Messenger (peace be upon him), seeking Allah\'s Pleasure. So our reward became due and sure with Allah. Some of us died without enjoying anything of their rewards (here), and one of them was Mus\'ab bin \'Umair who was martyred on the day of Uhud, and did not leave anything except a Namira (a sheet) in which he was shrouded. If we covered his head with it, his feet became naked, and if we covered his feet with it, his head became naked. So the Prophet (peace be upon him) said to us, "Cover his head with it and put some Idhkhir (grass) over his feet." But some amongst us have got the fruits of their labor ripened, and they are collecting them.',
          cite: 'Sahih al-Bukhari 4047, Sahih Muslim 940a',
        },
        {
          type: 'paragraph',
          text: 'That was exactly what scared him. He wondered if that ease meant his reward had already been paid out early, and in full, in this world, while companions like Hazrat Mus\'ab still had everything waiting for them. He cried, thinking that maybe he will be far from the Prophet (peace be upon him) in Akhirah because Allah has blessed him so much in this world.',
        },

        { type: 'heading', text: 'His Final Days' },
        {
          type: 'paragraph',
          text: 'This fear stayed with him until his very last days. When Hazrat Khabbab was dying, and his *kafan*, his burial shroud, was brought in front of him, he began to cry. People wondered if it was the fear of death. But it wasn\'t. It was out of the memory of his friend and the guilt. He thought of Mus\'ab, a man so beloved to Allah, who didn\'t even have enough cloth to cover his whole body when he was buried. And here was Hazrat Khabbab, decades later, with a shroud generous enough to wrap him completely, with room to spare. He didn\'t see that as a small blessing. He saw it as a question he couldn\'t answer yet. Had this world already given him everything it owed him while the next world was still waiting on men who deserved it more?',
        },
        {
          type: 'paragraph',
          text: 'Hazrat Khabbab ibn al-Aratt passed away of natural causes, as an old man, in Kufa. He wasn\'t martyred, even though for so many years of his youth, it seemed like martyrdom was the only outcome waiting for him. He was, by most accounts, the very first of the early Muhajireen to be buried there.',
        },

        { type: 'divider' },
        {
          type: 'paragraph',
          text: 'Hazrat Khabbab\'s story, for me, has been one of the most inspiring and heartbreakingly beautiful stories of the Sahabah I\'ve ever come across. We spend all our lives trying to compete with each other in wealth and power and status, get impressed by the ones who have the most in this world. And if all of this gets to us by Allah\'s blessing, we forget about everything else. Hazrat Khabbab, the more he earned wealth, the more he was scared of it. It came into his life, but it never settled in his heart. He knew that whatever he has in this world, not a single thing is of any benefit to him in Akhirah. He was scared that if he is so blessed here, he won\'t be blessed enough in Akhirah. (Even though he endured so much pain in his life to get through and be blessed, still the sight of worldly comfort and ease scared him so much.)',
        },
        {
          type: 'paragraph',
          text: 'I couldn\'t help but sit and wonder how blessed will the people be who are tested and tried in this world, especially in the cause of Allah. And how great are the people who have blessings in this world but still never let them settle in their hearts and pull them away from Allah. They see it as a test, so Allah can see what they do with it.',
        },
      ],
    },
];

// Sorted newest-first for convenience in UI
export const blogsByDate = [...blogs].sort(
  (a, b) => new Date(b.date) - new Date(a.date)
);

export const getBlogById = (id) => blogs.find((b) => b.id === id);
